// admin-analytics-expansion P4b (D9) — Ticket volume / net-revenue trend charts.
//
// Owns its own `getTicketTrends()` query (unlike OrgRollup/DistributionBar,
// which are purely presentational and receive already-fetched props) because
// it is mounted at TWO call sites with different parameters:
//   - Location tab (org_admin + supervisor): `locationId=effectiveLocationId`
//     -> per-location, location-timezone bucketing.
//   - Organization tab (org_admin only): no `locationId` -> org-wide, UTC
//     bucketing. Additionally gated by the `enabled` prop (mirrors the
//     `getOrgAnalytics` belt-and-suspenders `enabled: isOrgAdmin` safeguard in
//     AnalyticsPage) so the query is never issued for a role that would just
//     get rejected server-side.
//
// `getTicketTrends()`'s buckets are always fully zero-filled and sorted
// ascending (P4a + its tz-alignment review-fix) — safe to feed straight into
// the LineChart primitive with zero client-side gap-filling.
//
// P4b review-fix — the "no ticket activity" empty state is gated on
// `totalVolume === 0` for BOTH charts (true "nothing happened"), not on each
// chart's own total independently. `volume` (COUNT, unfiltered) and
// `netRevenueCents` (SUM FILTER paid) are independent aggregates (P4a D9) —
// a range with checked-in-but-unpaid tickets has `volume > 0` and
// `netRevenueCents === 0`, and the OLD per-chart gate falsely told the admin
// "No ticket activity" on the revenue card when there plainly was activity
// (it just hadn't been paid). When `totalVolume > 0` but revenue is
// genuinely all-zero, the revenue chart now renders a truthful flat-zero
// line instead of a misleading empty message.

import { getTicketTrends } from '@jq4race/api-client';
import { useTranslation } from '@jq4race/i18n';
import { DEFAULT_CURRENCY } from '@jq4race/shared';
import { formatCents, Skeleton } from '@jq4race/ui';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { TriangleAlert } from 'lucide-react';
import { translateApiError } from '../../../../shared/errors/translateApiError';
import { retryOnNonClientError } from '../../../../shared/query/retryOnNonClientError';
import { LineChart } from './ChartPrimitives';

const CURRENCY_FALLBACK = DEFAULT_CURRENCY;

// P4b review-fix (#2/#3) — shared Y-axis-tick + tooltip formatter for the
// revenue chart. Reuses the canonical `formatCents` helper (converting the
// chart's dollar-valued data back to cents for its cents-based API) so this
// stays the SAME single source of truth as every other currency display in
// the app (StatCard/OrgRollup/LocationBreakdownTable) — exported standalone
// so it can be unit-tested directly, since jsdom cannot reliably render (or
// let us assert on) recharts' actual Y-axis tick / tooltip DOM output.
export function formatRevenueTick(dollars: number): string {
  return formatCents(dollars * 100, CURRENCY_FALLBACK);
}

export interface TrendsSectionProps {
  /** Omit for an org-wide rollup (org_admin only, UTC bucketing). */
  locationId?: string;
  from: string;
  to: string;
  /** Belt-and-suspenders query gate (default: true). Set `false` to skip fetching entirely. */
  enabled?: boolean;
}

export function TrendsSection({ locationId, from, to, enabled = true }: TrendsSectionProps) {
  const { t } = useTranslation('analytics');
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['ticket-trends', locationId ?? 'org', from, to],
    queryFn: () => getTicketTrends({ locationId, from, to }),
    enabled,
    placeholderData: keepPreviousData,
    // FIX 3 (review) — `getTicketTrends` is gated behind the same
    // `advancedAnalytics` plan feature as `getOrgAnalytics` (see
    // OrgAnalyticsOverviewSection), so it can also legitimately 403. Don't
    // burn 3 retries + backoff on that expected outcome.
    retry: retryOnNonClientError,
  });

  if (!enabled) return null;

  const buckets = data?.buckets ?? [];
  const chartData = buckets.map((b) => ({
    day: b.day,
    volume: b.volume,
    netRevenue: b.netRevenueCents / 100,
  }));
  const totalVolume = buckets.reduce((sum, b) => sum + b.volume, 0);
  const totalRevenueCents = buckets.reduce((sum, b) => sum + b.netRevenueCents, 0);
  // Shared "nothing happened" gate for BOTH charts — see the module comment
  // above for why this must NOT be each chart's own independent total.
  const noActivity = buckets.length === 0 || totalVolume === 0;

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <TrendCard
        title={t('trends.volumePerDay')}
        subtitle={isLoading || isError ? undefined : t('trends.totalCount', { count: totalVolume })}
      >
        {isLoading ? (
          <TrendSkeleton />
        ) : isError ? (
          <TrendErrorState error={error} />
        ) : noActivity ? (
          <TrendEmptyState />
        ) : (
          <LineChart
            data={chartData}
            xKey="day"
            series={[{ dataKey: 'volume', label: t('trends.volumeSeriesLabel') }]}
            height={220}
          />
        )}
      </TrendCard>

      <TrendCard
        title={t('trends.revenuePerDay')}
        subtitle={
          isLoading || isError
            ? undefined
            : t('trends.totalAmount', {
                amount: formatCents(totalRevenueCents, CURRENCY_FALLBACK),
              })
        }
      >
        {isLoading ? (
          <TrendSkeleton />
        ) : isError ? (
          <TrendErrorState error={error} />
        ) : noActivity ? (
          <TrendEmptyState />
        ) : (
          <LineChart
            data={chartData}
            xKey="day"
            series={[{ dataKey: 'netRevenue', label: t('trends.revenueSeriesLabel') }]}
            height={220}
            yAxisAllowDecimals
            valueFormatter={formatRevenueTick}
          />
        )}
      </TrendCard>
    </div>
  );
}

// ── Sub-components ──────────────────────────────────────────────────────────

function TrendCard({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-sm border border-jq-sand/70 bg-jq-surface-0 p-4 shadow-sm">
      <div className="flex items-baseline justify-between gap-2">
        <h3 className="text-[11px] font-medium uppercase tracking-[0.18em] text-jq-graphite">
          {title}
        </h3>
        {subtitle && <span className="text-[12px] text-jq-stone">{subtitle}</span>}
      </div>
      <div className="mt-3">{children}</div>
    </div>
  );
}

function TrendSkeleton() {
  return <Skeleton className="h-[220px] w-full" aria-hidden />;
}

function TrendEmptyState() {
  const { t } = useTranslation('analytics');
  return (
    <p className="flex h-[220px] items-center justify-center text-center text-[13px] text-jq-stone">
      {t('emptyStates.noTicketActivity')}
    </p>
  );
}

function TrendErrorState({ error }: { error: Error | null }) {
  const { t } = useTranslation('analytics');
  const message = translateApiError(error, t, { fallback: t('errors.trendsLoadError') });
  return (
    <div
      role="alert"
      className="flex h-[220px] items-start gap-3 rounded-sm border border-jq-danger/40 bg-jq-danger/10 px-4 py-4 text-[13px] text-jq-danger"
    >
      <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.75} />
      <p className="leading-snug">{message}</p>
    </div>
  );
}
